import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BottomSheet } from "./BottomSheet";
import { KairosNameExplanation } from "./KairosNameExplanation";
import kairosLogoWithName from "../images/kairos_logo_with_name.png";
import "./KairosLandingPage.css";

interface FeatureItem {
  id: string;
  title: string;
  description: string;
  accent: string;
  surface: string;
  border: string;
}

const features: FeatureItem[] = [
  {
    id: "neural-sync",
    title: "Neural Job-Resume Sync",
    description:
      "Our proprietary OpenAI-powered neural networks perform real-time analysis of job descriptions against your entire resume portfolio, instantly calculating fit scores and recommending optimal resume versions with surgical precision. Automatically analyzes each resume against job requirements, providing detailed match scores for skills, experience, and education, along with actionable recommendations to improve your fit.",
    accent: "#c084fc",
    surface: "rgba(192, 132, 252, 0.12)",
    border: "rgba(192, 132, 252, 0.28)",
  },
  {
    id: "job-board",
    title: "Universal Job Board Integration",
    description:
      "Seamlessly integrates with LinkedIn, Glassdoor, Indeed, Monster, ZipRecruiter, and all major job platforms through our intelligent browser extension, providing instant career intelligence across the entire digital employment ecosystem. Extract job postings from any website with one click, automatically parse job descriptions using AI, and create job applications directly from web pages.",
    accent: "#67e8f9",
    surface: "rgba(103, 232, 249, 0.12)",
    border: "rgba(103, 232, 249, 0.28)",
  },
  {
    id: "application-tracking",
    title: "Application Tracking",
    description:
      "Advanced application lifecycle management with real-time tracking, contact management, and interview preparation intelligence. Track applications through every stage from Initialized to Applied, Interviewing, Offer, and beyond. Manage contacts, add notes, and monitor your application pipeline with comprehensive analytics and insights.",
    accent: "#4ade80",
    surface: "rgba(74, 222, 128, 0.12)",
    border: "rgba(74, 222, 128, 0.28)",
  },
  {
    id: "interview-intelligence",
    title: "Interview Intelligence",
    description:
      "Comprehensive interview preparation covering all stages from initial screening to final rounds. Company-specific insights powered by AI analysis of public information, including recent layoffs, hirings, management changes, scandals, employee reviews, and customer feedback. Strategic networking recommendations and contact extraction help you prepare for every interview with confidence.",
    accent: "#fb923c",
    surface: "rgba(251, 146, 60, 0.12)",
    border: "rgba(251, 146, 60, 0.28)",
  },
  {
    id: "company-search",
    title: "Company Search",
    description:
      "Deep company intelligence gathering that analyzes layoffs, hirings, scandals, management changes, employee reviews, and customer feedback from multiple sources. Get comprehensive company profiles with Glassdoor ratings, LinkedIn employee counts, recent news, and strategic insights to make informed career decisions.",
    accent: "#60a5fa",
    surface: "rgba(96, 165, 250, 0.12)",
    border: "rgba(96, 165, 250, 0.28)",
  },
  {
    id: "resume-portfolio",
    title: "Resume Portfolio",
    description:
      "Manage multiple resumes with ATS-optimized parsing and revision tracking. Upload and maintain different resume versions tailored for different roles, with automatic analysis of how each resume matches against specific job requirements. Track revisions and optimize your resume portfolio for maximum application success.",
    accent: "#2dd4bf",
    surface: "rgba(45, 212, 191, 0.12)",
    border: "rgba(45, 212, 191, 0.28)",
  },
];

interface HelpSection {
  id: string;
  title: string;
  content: string;
  benefits: string[];
}

const helpSections: HelpSection[] = [
  {
    id: "what-is-kairos",
    title: "What is Kairos?",
    content:
      "Kairos is the world's most advanced AI-powered career intelligence platform that transforms how you search for jobs, optimize your resume, and land your dream position. Think of it as your personal career strategist, available 24/7, powered by cutting-edge neural networks that understand both job markets and your unique professional profile.",
    benefits: [
      "AI analyzes thousands of job postings in seconds",
      "Automatically matches your skills to opportunities",
      "Provides actionable insights to improve your applications",
      "Tracks your entire job search journey in one place",
    ],
  },
  {
    id: "why-use-kairos",
    title: "Why Use Kairos?",
    content:
      "Traditional job searching is broken. You spend hours crafting resumes, only to get rejected by ATS systems. You apply to hundreds of jobs with no visibility into your success rate. You prepare for interviews blind, without knowing what the company really values. Kairos changes all of that.",
    benefits: [
      "Automate repetitive capture, parsing, and formatting work",
      "See how each posting maps to your skills and experience",
      "Pick the right resume variant with clear fit reasoning",
      "Review public company signals before you talk to a recruiter",
      "Keep stages, contacts, and next steps attached to every role",
    ],
  },
  {
    id: "how-it-works",
    title: "How It Works",
    content:
      "Kairos uses proprietary neural networks to analyze job descriptions against your entire resume portfolio. Our AI understands context, not just keywords. It calculates precise fit scores, identifies skill gaps, and recommends the optimal resume version for each position. All while you browse job boards naturally.",
    benefits: [
      "Upload your resumes once, use them everywhere",
      "Browser extension extracts jobs with one click",
      "AI analyzes fit in real-time as you browse",
      "Get instant recommendations and insights",
      "Track applications through every stage",
    ],
  },
  {
    id: "key-features",
    title: "Key Features",
    content:
      "Kairos isn't just another job board aggregator. It's a complete career intelligence ecosystem that gives you superhuman capabilities in your job search. From neural resume matching to company intelligence gathering, every feature is designed to give you a competitive edge.",
    benefits: [
      "Neural Job-Resume Sync: AI-powered matching",
      "Universal Job Board Integration: Works everywhere",
      "Application Tracking: Never lose an opportunity",
      "Interview Intelligence: Prepare with confidence",
      "Company Search: Know before you apply",
      "Resume Portfolio: Multiple versions, optimized",
    ],
  },
  {
    id: "success-stories",
    title: "Decisions worth your time",
    content:
      "Kairos compresses guesswork: compare resumes to real postings, pressure-test fit with transparent scoring, and keep every touchpoint organized so you spend energy on roles worth pursuing—not on scattered tabs, duplicate drafts, and lost context.",
    benefits: [
      "Compare your strongest resume version before you apply",
      "Understand fit, gaps, and positioning for each posting",
      "Research companies with structured context instead of random tabs",
      "Track applications, contacts, and follow-ups in one pipeline",
      "Revisit choices with notes and history tied to each role",
    ],
  },
];

const navigationItems = [
  { id: "overview", label: "Overview" },
  { id: "features", label: "Features" },
  { id: "workflow", label: "How It Works" },
  { id: "results", label: "Results" },
  { id: "support", label: "Support" },
];

const workflowSteps = [
  {
    title: "Build your resume base once",
    description:
      "Upload and organize multiple resume versions so Kairos always has the right material to compare against each opening.",
  },
  {
    title: "Capture jobs where you already browse",
    description:
      "Use the browser extension or manual entry to bring openings into one system instead of juggling tabs, notes, and screenshots.",
  },
  {
    title: "Score fit and uncover the gaps",
    description:
      "Review match strength, skill gaps, company context, and targeted resume recommendations before you spend time applying.",
  },
  {
    title: "Track the full hiring pipeline",
    description:
      "Move each role through application, interview, and offer stages while keeping contacts, notes, and next steps attached.",
  },
];

const heroHighlights = [
  { value: "Fit-first", label: "score roles before the long apply" },
  { value: "Full context", label: "resumes, intel, and pipeline together" },
  { value: "One workflow", label: "from saved posting to follow-up" },
];

interface KairosLandingPageProps {
  onLoginClick?: () => void;
  onSupportClick?: () => void;
  loginDisabled?: boolean;
}

interface LandingLoginButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  label?: string;
  /** Shown instead of `label` under `max-width: 767px` when used with `.kairos-header-login` */
  responsiveShortLabel?: string;
  variant?: "primary" | "secondary";
  className?: string;
}

const LandingLoginButton: React.FC<LandingLoginButtonProps> = ({
  onClick,
  disabled = false,
  label = "Login / Signup",
  responsiveShortLabel,
  variant = "primary",
  className = "",
}) => {
  if (!onClick) {
    return null;
  }

  const classes = [
    "kairos-login-button",
    variant === "secondary" ? "kairos-login-button--secondary" : "",
    disabled ? "kairos-login-button--disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={
        disabled ? "Login is temporarily disabled during maintenance" : label
      }
      className={classes}
    >
      {responsiveShortLabel ? (
        <>
          <span className="kairos-login-label-long">{label}</span>
          <span className="kairos-login-label-short">
            {responsiveShortLabel}
          </span>
        </>
      ) : (
        <span>{label}</span>
      )}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 7l5 5m0 0l-5 5m5-5H6"
        />
      </svg>
    </button>
  );
};

export const KairosLandingPage: React.FC<KairosLandingPageProps> = ({
  onLoginClick,
  onSupportClick,
  loginDisabled = false,
}) => {
  const [showKairosInfo, setShowKairosInfo] = useState(false);

  const overviewSection = helpSections.find(
    (section) => section.id === "what-is-kairos",
  );
  const whyKairosSection = helpSections.find(
    (section) => section.id === "why-use-kairos",
  );
  const featureSummarySection = helpSections.find(
    (section) => section.id === "key-features",
  );
  const workflowSection = helpSections.find(
    (section) => section.id === "how-it-works",
  );
  const resultsSection = helpSections.find(
    (section) => section.id === "success-stories",
  );
  const storySections = helpSections.filter((section) =>
    ["what-is-kairos", "why-use-kairos", "how-it-works"].includes(section.id),
  );

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section className="kairos-landing-page">
      <div className="kairos-landing-backdrop" aria-hidden="true" />

      <header className="kairos-landing-header">
        <button
          type="button"
          onClick={() => setShowKairosInfo(true)}
          className="kairos-brand-button"
          aria-label="Open Kairos overview"
        >
          <img
            src={kairosLogoWithName}
            alt="Kairos"
            className="kairos-brand-logo"
          />
        </button>

        <nav className="kairos-landing-nav" aria-label="Landing page sections">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="kairos-nav-link"
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="kairos-header-actions">
          <a
            href="https://cognixisai.com"
            className="kairos-header-link"
            target="_self"
          >
            Cognixis AI
          </a>
          <LandingLoginButton
            onClick={onLoginClick}
            disabled={loginDisabled}
            className="kairos-header-login"
            responsiveShortLabel="Log in"
          />
        </div>
      </header>

      <main className="kairos-landing-main">
        <section
          id="overview"
          className="kairos-hero kairos-scroll-anchor"
          aria-labelledby="kairos-hero-heading"
        >
          <div className="kairos-hero-copy">
            <div className="kairos-hero-copy-lead">
              <p className="kairos-eyebrow">Career intelligence platform</p>
              <h1 id="kairos-hero-heading" className="kairos-hero-title">
                Find the right role at the right moment.
              </h1>
              <p className="kairos-hero-description">
                {overviewSection?.content}
              </p>
            </div>

            <div className="kairos-hero-copy-foot">
              <div className="kairos-hero-actions">
                <LandingLoginButton
                  onClick={onLoginClick}
                  disabled={loginDisabled}
                  label="Open Login / Signup"
                />
                <button
                  type="button"
                  className="kairos-secondary-button"
                  onClick={() => scrollToSection("features")}
                >
                  Explore the page
                </button>
              </div>

              <div className="kairos-inline-actions">
                <button
                  type="button"
                  className="kairos-inline-link"
                  onClick={() => setShowKairosInfo(true)}
                >
                  What does Kairos mean?
                </button>
                {onSupportClick && (
                  <button
                    type="button"
                    className="kairos-inline-link"
                    onClick={onSupportClick}
                  >
                    Contact support
                  </button>
                )}
              </div>
            </div>
          </div>

          <aside className="kairos-hero-panel">
            <div className="kairos-panel-intro">
              <p className="kairos-panel-kicker">One continuous workflow</p>
              <h2>Everything from discovery to follow-up in one place.</h2>
              <p>{whyKairosSection?.content}</p>
            </div>

            <div className="kairos-hero-metrics">
              {heroHighlights.map((highlight) => (
                <div key={highlight.label} className="kairos-metric-tile">
                  <strong>{highlight.value}</strong>
                  <span>{highlight.label}</span>
                </div>
              ))}
            </div>

            <ol className="kairos-preview-list">
              {workflowSteps.map((step, index) => (
                <li key={step.title} className="kairos-preview-item">
                  <span className="kairos-preview-step">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </aside>

          <div className="kairos-signal-strip" aria-label="Platform benefits">
            {whyKairosSection?.benefits.map((benefit) => (
              <div key={benefit} className="kairos-signal-chip">
                {benefit}
              </div>
            ))}
          </div>
        </section>

        <section className="kairos-section">
          <div className="kairos-section-heading">
            <p className="kairos-eyebrow">Why Kairos</p>
            <h2>
              Keep every part of the job search on one page and in one system.
            </h2>
            <p>{featureSummarySection?.content}</p>
          </div>

          <div className="kairos-story-grid">
            {storySections.map((section) => (
              <article key={section.id} className="kairos-story-panel">
                <div className="kairos-story-copy">
                  <p className="kairos-story-kicker">{section.title}</p>
                  <p className="kairos-story-text">{section.content}</p>
                </div>
                <ul className="kairos-story-list">
                  {section.benefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section id="features" className="kairos-section kairos-scroll-anchor">
          <div className="kairos-section-heading">
            <p className="kairos-eyebrow">Platform capabilities</p>
            <h2>{featureSummarySection?.title}</h2>
            <p>{featureSummarySection?.content}</p>
          </div>

          <div className="kairos-feature-list">
            {features.map((feature, index) => (
              <article
                key={feature.id}
                className="kairos-feature-row"
                style={{
                  background: `linear-gradient(180deg, ${feature.surface} 0%, rgba(15, 23, 42, 0.78) 100%)`,
                  borderColor: feature.border,
                }}
              >
                <div className="kairos-feature-index-wrap">
                  <span
                    className="kairos-feature-index"
                    style={{ color: feature.accent }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="kairos-feature-body">
                  <span
                    className="kairos-feature-accent"
                    aria-hidden="true"
                    style={{ background: feature.accent }}
                  />
                  <p className="kairos-feature-kicker">Capability</p>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          id="workflow"
          className="kairos-section kairos-workflow-section kairos-scroll-anchor"
        >
          <div className="kairos-section-heading">
            <p className="kairos-eyebrow">How it works</p>
            <h2>{workflowSection?.title}</h2>
            <p>{workflowSection?.content}</p>
          </div>

          <div className="kairos-workflow-grid">
            {workflowSteps.map((step, index) => (
              <article key={step.title} className="kairos-workflow-card">
                <span className="kairos-workflow-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>

          <div className="kairos-support-grid">
            {workflowSection?.benefits.map((benefit) => (
              <div key={benefit} className="kairos-support-item">
                {benefit}
              </div>
            ))}
          </div>
        </section>

        <section
          id="results"
          className="kairos-section kairos-results-section kairos-scroll-anchor"
        >
          <div className="kairos-results-copy">
            <p className="kairos-eyebrow">Where Kairos focuses</p>
            <h2>{resultsSection?.title}</h2>
            <p>{resultsSection?.content}</p>
          </div>

          <div className="kairos-results-grid">
            {resultsSection?.benefits.map((benefit) => (
              <article key={benefit} className="kairos-result-card">
                <p>{benefit}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="support"
          className="kairos-section kairos-final-section kairos-scroll-anchor"
        >
          <div className="kairos-final-copy">
            <p className="kairos-eyebrow">Ready when you are</p>
            <h2>Scroll the whole story, then sign in whenever you want.</h2>
            <p>
              The login button stays pinned in the header while you browse, and
              this section gives you the same next step once you reach the end.
            </p>
          </div>

          <div className="kairos-final-actions">
            <LandingLoginButton
              onClick={onLoginClick}
              disabled={loginDisabled}
              label="Login / Signup"
            />
            {onSupportClick && (
              <button
                type="button"
                className="kairos-secondary-button"
                onClick={onSupportClick}
              >
                Contact Support
              </button>
            )}
          </div>

          <div className="kairos-footer-links">
            <Link to="/help">Help</Link>
            <Link to="/terms-of-service">Terms</Link>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/cookie-policy">Cookies</Link>
          </div>
        </section>
      </main>

      <footer className="kairos-footer">
        <a href="https://cognixisai.com" className="kairos-footer-home-link">
          Back to Cognixis AI
        </a>
      </footer>

      <BottomSheet
        isOpen={showKairosInfo}
        onClose={() => setShowKairosInfo(false)}
        title="What is Kairos?"
      >
        <KairosNameExplanation />
      </BottomSheet>
    </section>
  );
};
